/**
 * Place-on-Hold: brand-optional enhancements to the existing `billingHoldUntil` billing hold
 * (#33568 / S1 — #33624).
 *
 * There is ONE hold — `policy.billingHoldUntil`. Staff place it through the Payment tab; the renewal
 * creator also places it itself on the first renewal of a migrated member-billed policy, stamping
 * `billingHoldPlacedBy: 'system'` rather than a username (#35280, see migrated-renewal-billing.ts). This
 * module does not introduce a new hold concept; it adds behavior and brand-config-gated options on top
 * of the existing field:
 *   - an active hold suppresses all automated billing + invoicing (extends today's next-charge skip;
 *     a no-op for brands like Branch that have no direct-bill / invoicing beyond install pay);
 *   - mutual brands may additionally require a reason and offer a "hold cancellations as well" option
 *     (`billingHoldBlocksCancellations`) that stops automated cancellation-for-nonpayment.
 *
 * The hold auto-releases by expiry — once `billingHoldUntil` passes, everything resumes; no release job
 * is needed. Lives in `@ourbranch/policy-utils` (audience: universal) so staff-fe, the resolver, and the
 * AR/cancel crons share the same predicates.
 */
import { addDays, addMonths, parseISO, format } from 'date-fns';
import { getTodayStr } from '@ourbranch/date-fns-helpers';
import { policyTermMonths } from '@ourbranch/be-constants';
import { paymentType as paymentTypeLookup } from '@ourbranch/lookups';

/** The billing-hold fields carried on a policy. All additive / nullable. */
export interface BillingHoldFields {
  /** AWSDate (yyyy-MM-dd). The existing field — the hold is in effect through this date. */
  billingHoldUntil?: string | null;
  billingHoldReason?: string | null;
  /** Mutual-only: when true, automated cancellation-for-nonpay is suppressed while the hold is active. */
  billingHoldBlocksCancellations?: boolean | null;
  /** Staff username, or 'system' when the platform placed the hold (SYSTEM_BILLING_HOLD_PLACED_BY). */
  billingHoldPlacedBy?: string | null;
  /** ISO timestamp. */
  billingHoldPlacedAt?: string | null;
}

/**
 * True while a billing hold is in effect. Compared as AWSDate strings (`yyyy-MM-dd` order
 * chronologically as text). The caller must pass a STATE-AWARE business date for `todayStr`
 * (e.g. `getTodayStr(policy.state)` from `@ourbranch/date-fns-helpers`), so a Lambda's default UTC clock
 * does not release a hold a day early/late. Drives suppression of all automated billing + invoicing.
 */
export function isBillingHoldActive(policy: BillingHoldFields | null | undefined, todayStr: string): boolean {
  return !!policy?.billingHoldUntil && todayStr <= policy.billingHoldUntil;
}

/**
 * Convenience for backend AR/billing jobs that have the policy loaded and just need a yes/no: is a
 * billing hold active as-of today in the policy's state timezone. Resolves the state-aware "today"
 * (`getTodayStr(policy.state)`) so callers don't each re-derive it. Drives suppression of automated
 * billing + invoicing while a hold is in effect.
 */
export function isPolicyBillingHoldActive(
  policy: (BillingHoldFields & { state?: string | null }) | null | undefined
): boolean {
  return isBillingHoldActive(policy, getTodayStr(policy?.state));
}

/**
 * True while an active hold also blocks automated cancellation-for-nonpayment. Only mutual brands can
 * set `billingHoldBlocksCancellations` (the staff checkbox is brand-gated), so this is always false for
 * brands like Branch.
 */
export function holdBlocksCancellations(policy: BillingHoldFields | null | undefined, todayStr: string): boolean {
  return isBillingHoldActive(policy, todayStr) && policy?.billingHoldBlocksCancellations === true;
}

/**
 * Max days a hold placed by this user may run: the brand's `teamLeaderMaxDays` for a team leader (mutuals
 * 30, #37845), else `maxDays`. Shared by staff-fe and policy-mod so the picker and the server agree.
 */
export function getBillingHoldMaxDays(
  { maxDays, teamLeaderMaxDays }: { maxDays: number; teamLeaderMaxDays?: number },
  { isTeamLeader }: { isTeamLeader: boolean }
): number {
  return isTeamLeader && teamLeaderMaxDays ? teamLeaderMaxDays : maxDays;
}

/** The latest date a hold may be set to, given a state-aware `todayStr` and the brand's max-days cap. */
export function getBillingHoldMaxDate(todayStr: string, maxHoldDays: number): string {
  return format(addDays(parseISO(todayStr), maxHoldDays), 'yyyy-MM-dd');
}

export interface PlaceOnHoldInput {
  billingHoldUntil?: string | null;
  billingHoldReason?: string | null;
  billingHoldBlocksCancellations?: boolean | null;
}

export interface PlaceOnHoldBrandConfig {
  /** Business "today" in the policy's state timezone (e.g. `getTodayStr(policy.state)`). */
  todayStr: string;
  /** Max days a hold may run (brand config; Branch 8, mutuals 15). */
  maxHoldDays: number;
  /** Whether a reason is required (brand config; mutuals on, Branch off). */
  requireReason?: boolean;
  /** Whether the "hold cancellations as well" option is available (brand config; mutuals on). */
  holdCancellationsEnabled?: boolean;
}

/**
 * Validates a Place-on-Hold request against brand config. Returns user-facing error messages; an empty
 * list means valid. Pure (no I/O) so staff-fe and the resolver share it.
 */
export function validatePlaceOnHold(
  input: PlaceOnHoldInput,
  { todayStr, maxHoldDays, requireReason, holdCancellationsEnabled }: PlaceOnHoldBrandConfig
): string[] {
  const errors: string[] = [];

  if (!input.billingHoldUntil) {
    errors.push('A hold date is required.');
  } else {
    if (input.billingHoldUntil < todayStr) {
      errors.push('Hold date cannot be in the past.');
    }
    if (input.billingHoldUntil > getBillingHoldMaxDate(todayStr, maxHoldDays)) {
      errors.push(`Hold date cannot be more than ${maxHoldDays} days out.`);
    }
  }

  if (requireReason && !input.billingHoldReason?.trim()) {
    errors.push('Hold Reason is required.');
  }

  if (input.billingHoldBlocksCancellations && !holdCancellationsEnabled) {
    errors.push('Holding cancellations is not enabled for this brand.');
  }

  return errors;
}

/**
 * System Pay Ahead billing hold (#35830).
 *
 * Staff → Billing → Pay Ahead charges the next installment manually, then places a billing hold so the
 * already-scheduled auto-draft for that installment is not collected a second time. The hold does NOT
 * "skip" that draft — @ourbranch/billing *defers* the next auto-charge to `billingHoldUntil` (see
 * payment.service getPaymentV2: `chargeDate = billingHoldUntil` while the hold is active). To push the
 * deferred draft onto the next REAL installment (the one after the one just paid), the hold must run to
 * `nextPayments[1].date` — typically one full installment interval past the next draft, so ~30-60 days
 * out on a monthly plan. That is well beyond the mutual flat `maxDays` cap (15), which is why a normal
 * Place-on-Hold can't express it and why pay-ahead holds were rejected on mutual brands after #33697.
 *
 * This reason string is the system-origin signal (mirrors MIGRATED_RENEWAL_FIRST_INSTALLMENT_HOLD_REASON):
 * the confirm resolver treats a hold carrying EXACTLY this reason as a system pay-ahead hold that bypasses
 * the `CanManageBillingHolds` authority gate and the `maxDays` cap, but still bounds the date to roughly
 * two installment intervals (getPayAheadMaxHoldDate) so the carve-out can't place an arbitrary-length hold.
 * Branch is non-enhanced, so it never reaches that gate and is unaffected.
 */
export const PAY_AHEAD_BILLING_HOLD_REASON = 'System: Pay Ahead — skip already-paid installment';

/** True when a hold carries the system Pay Ahead reason — the system-origin signal (#35830). */
export function isPayAheadBillingHold(policy: BillingHoldFields | null | undefined): boolean {
  return policy?.billingHoldReason === PAY_AHEAD_BILLING_HOLD_REASON;
}

/**
 * Slack (in days) added to the two-interval ceiling for a system pay-ahead hold, so a hold landing on the
 * next real installment date passes despite month-length / billing-day-of-month drift (the installment
 * lands on a fixed day of the month, which can be a few days past an exact N-month offset from today).
 */
export const PAY_AHEAD_HOLD_MARGIN_DAYS = 10;

/**
 * Months between installments for a plan: Monthly = 1, QuarterTerm = term/4, HalfTerm = term/2. Mirrors
 * @ourbranch/billing getPaymentIntervalMonths (policy-utils can't depend on billing without a cycle). Any
 * non-installment / unknown plan falls back to 1 — the conservative (smallest) interval.
 */
export function getPaymentIntervalMonths(paymentType: string | null | undefined, policyType: string): number {
  const termMonths = policyTermMonths[policyType] ?? 12;
  if (paymentType === paymentTypeLookup.QuarterTerm) {
    return Math.floor(termMonths / 4);
  }
  if (paymentType === paymentTypeLookup.HalfTerm) {
    return Math.floor(termMonths / 2);
  }
  return 1;
}

/**
 * Upper bound a system pay-ahead hold may run to: `today + 2 installment intervals + margin`. The hold
 * must reach `nextPayments[1].date` (the draft after the one just paid), which is at most two intervals
 * out from today; the margin absorbs billing-day drift. Anything beyond this is not a legitimate
 * pay-ahead hold, so the confirm resolver rejects it even though the pay-ahead carve-out skips the flat
 * `maxDays` cap. State-aware `todayStr` must be passed (e.g. getTodayStr(policy.state)).
 */
export function getPayAheadMaxHoldDate(
  todayStr: string,
  paymentType: string | null | undefined,
  policyType: string,
  marginDays: number = PAY_AHEAD_HOLD_MARGIN_DAYS
): string {
  const intervalMonths = Math.max(1, getPaymentIntervalMonths(paymentType, policyType));
  return format(addDays(addMonths(parseISO(todayStr), 2 * intervalMonths), marginDays), 'yyyy-MM-dd');
}

export interface PayAheadHoldBound {
  /** Business "today" in the policy's state timezone (e.g. getTodayStr(policy.state)). */
  todayStr: string;
  /** Upper bound from getPayAheadMaxHoldDate; required — a missing bound rejects the date. */
  payAheadMaxHoldDate?: string | null;
}

/**
 * Validates a system pay-ahead hold: the date must be present, not in the past, and within the
 * two-interval bound (NOT the flat maxDays cap), and the hold may not block cancellations. The reason
 * requirement is already satisfied by PAY_AHEAD_BILLING_HOLD_REASON. Returns user-facing errors; empty
 * means valid. Pure, so staff-fe and the resolver share it. (#35830)
 */
export function validatePayAheadHold(
  input: PlaceOnHoldInput,
  { todayStr, payAheadMaxHoldDate }: PayAheadHoldBound
): string[] {
  const errors: string[] = [];

  if (!input.billingHoldUntil) {
    errors.push('A hold date is required.');
  } else {
    if (input.billingHoldUntil < todayStr) {
      errors.push('Hold date cannot be in the past.');
    }
    if (!payAheadMaxHoldDate || input.billingHoldUntil > payAheadMaxHoldDate) {
      errors.push('Pay Ahead hold date is outside the allowed window.');
    }
  }

  if (input.billingHoldBlocksCancellations) {
    errors.push('A Pay Ahead hold cannot block cancellations.');
  }

  return errors;
}

/**
 * Endorsement bill window (#37871). On a brand with billingAutomation.endorsementBillWindow (the mutuals) an
 * endorsement increase is never collected when the change is issued. The policy-change preview places this
 * hold so card/ACH drafts the money no sooner than ENDORSEMENT_DRAFT_WINDOW_DAYS after the change (the billing
 * cron's usual pre-draft notice goes out before the hold ends), and a direct-bill invoice sent at confirm is
 * due ENDORSEMENT_INVOICE_DUE_DAYS out. Like the Pay Ahead hold, the reason is the system-origin signal: the
 * confirm resolver lets the hold through without CanManageBillingHolds and past the flat maxDays cap, bounded
 * to ENDORSEMENT_INVOICE_DUE_DAYS from today.
 */
export const ENDORSEMENT_DRAFT_WINDOW_DAYS = 15;
export const ENDORSEMENT_INVOICE_DUE_DAYS = 30;
export const ENDORSEMENT_BILL_WINDOW_HOLD_REASON = 'System: endorsement bill window — nothing collected at issue';

/** True when a hold carries the system endorsement bill window reason (#37871). */
export function isEndorsementBillWindowHold(policy: BillingHoldFields | null | undefined): boolean {
  return policy?.billingHoldReason === ENDORSEMENT_BILL_WINDOW_HOLD_REASON;
}

/** Validates a system endorsement bill window hold (#37871). Empty means valid. */
export function validateEndorsementBillWindowHold(
  input: PlaceOnHoldInput,
  { todayStr }: { todayStr: string }
): string[] {
  const errors: string[] = [];
  const maxHoldDate = format(addDays(parseISO(todayStr), ENDORSEMENT_INVOICE_DUE_DAYS), 'yyyy-MM-dd');
  if (!input.billingHoldUntil) {
    errors.push('A hold date is required.');
  } else if (input.billingHoldUntil < todayStr || input.billingHoldUntil > maxHoldDate) {
    errors.push('Endorsement bill window hold date is outside the allowed window.');
  }
  if (input.billingHoldBlocksCancellations) {
    errors.push('An endorsement bill window hold cannot block cancellations.');
  }
  return errors;
}
